import { roundTo2Decimals } from '@principle-theorem/accounting';
import { groupBy, sumBy } from 'lodash';
import { type IExactV2InvoiceLine } from '../../../source/entities/patient-invoice-lines-v2';
import { type IExactV2Transaction } from '../../../source/entities/patient-transactions-v2';
import {
  ExactV2AdjustmentType,
  ExactV2TransactionType,
} from '../../../lib/exact-v2-transaction-type';
import { type IExactV2TransactionAllocation } from '../../../source/entities/transaction-allocations-v2';

/** Exact leaves sub-cent GST residuals on settled rows. Rounding, not debt. */
export const EXACT_MINIMUM_OWING = 0.05;

interface ICategorisedTransactions {
  charges: IExactV2Transaction[];
  deposits: IExactV2Transaction[];
}

const paymentRowTypes = [
  ExactV2TransactionType.Charge,
  ExactV2TransactionType.PaymentPlan,
  ExactV2TransactionType.Receipt,
  ExactV2TransactionType.Adjustment,
  ExactV2TransactionType.TransferIn,
  ExactV2TransactionType.TransferOut,
  ExactV2TransactionType.WriteOff,
  ExactV2TransactionType.Discount,
  ExactV2TransactionType.ExternalCredit,
];

/**
 * Read-side view over a patient's raw Exact ledger rows: what each row is,
 * which charges are billable, and how rows relate via allocations and
 * invoice line groups.
 */
export class ExactTransactionLedger {
  private readonly _allocations: IExactV2TransactionAllocation[];
  private readonly _allocationsByChargeKey: Map<
    string,
    IExactV2TransactionAllocation[]
  >;
  private readonly _paymentRowsByKey: Map<string, IExactV2Transaction>;
  private readonly _invoiceLinesByGroupId: Map<string, IExactV2InvoiceLine[]>;
  private readonly _categorised: ICategorisedTransactions;

  constructor(
    private _transactions: IExactV2Transaction[],
    invoiceLines: IExactV2InvoiceLine[],
    allocations: IExactV2TransactionAllocation[]
  ) {
    this._allocations = allocations.filter(
      (allocation) => !ExactTransactionLedger._isSelfAllocation(allocation)
    );
    this._allocationsByChargeKey = this._buildAllocationIndex();
    this._paymentRowsByKey = this._buildPaymentRowIndex();
    this._invoiceLinesByGroupId =
      ExactTransactionLedger._buildInvoiceLineIndex(invoiceLines);
    this._categorised = this._categoriseTransactions();
  }

  /**
   * Resubmission adjustments are write-offs of an earlier charge. They live
   * on the payment side of the allocation table and are only billed when
   * they re-bill spent credit.
   */
  static isResubmissionAdjustment(transaction: IExactV2Transaction): boolean {
    return (
      transaction.transactionType === ExactV2TransactionType.Adjustment &&
      transaction.isResubmissionAdjustment === true
    );
  }

  /** Transfers move a balance between accounts and carry no line breakdown. */
  static isTransfer(transaction: IExactV2Transaction): boolean {
    return (
      transaction.transactionType === ExactV2TransactionType.TransferIn ||
      transaction.transactionType === ExactV2TransactionType.TransferOut
    );
  }

  /** Changes what the patient was billed, so it is treated as a charge. */
  static isInvoiceSideAdjustment(transaction: IExactV2Transaction): boolean {
    return (
      transaction.transactionType === ExactV2TransactionType.Adjustment &&
      transaction.adjustmentType === ExactV2AdjustmentType.InvoiceSide
    );
  }

  /** Corrects money the practice already took, so it is treated as a payment. */
  static isReceiptSideAdjustment(transaction: IExactV2Transaction): boolean {
    return (
      transaction.transactionType === ExactV2TransactionType.Adjustment &&
      transaction.adjustmentType === ExactV2AdjustmentType.ReceiptSide
    );
  }

  /**
   * Exact reuses `_record_id` across rows, so a transaction is only uniquely
   * identified by its id together with its ledger date. Allocations must be
   * matched on both, or one row inherits another row's allocations.
   */
  private static _transactionKey(id: string, date: string): string {
    return `${id}-${date}`;
  }

  /** Exact allocates a positive resubmission against itself, which moves no money. */
  private static _isSelfAllocation(
    allocation: IExactV2TransactionAllocation
  ): boolean {
    return (
      allocation.paymentTransactionId === allocation.chargeTransactionId &&
      allocation.paymentDate === allocation.chargeDate
    );
  }

  private static _buildInvoiceLineIndex(
    invoiceLines: IExactV2InvoiceLine[]
  ): Map<string, IExactV2InvoiceLine[]> {
    const index = new Map<string, IExactV2InvoiceLine[]>();
    for (const line of invoiceLines) {
      // Exact reuses a line group id across a quote and the charge billed from it.
      if (
        !line.invoiceLineGroupId ||
        line.transactionType !== ExactV2TransactionType.Charge
      ) {
        continue;
      }
      const existing = index.get(line.invoiceLineGroupId) ?? [];
      existing.push(line);
      index.set(line.invoiceLineGroupId, existing);
    }
    return index;
  }

  categorise(): ICategorisedTransactions {
    return this._categorised;
  }

  /** Charges that should be emitted as invoice lines. */
  billableCharges(): IExactV2Transaction[] {
    const chargeKeysUsedAsPayments = this._collectChargeKeysUsedAsPayments();
    const cancelledLineGroupIds = this._getCancelledLineGroupIds();

    return this.categorise().charges.filter((charge) => {
      const isCancelled =
        charge.invoiceLineGroupId !== undefined &&
        cancelledLineGroupIds.has(charge.invoiceLineGroupId);
      if (
        chargeKeysUsedAsPayments.has(
          ExactTransactionLedger._transactionKey(charge.id, charge.date)
        ) ||
        this._isUnspentCreditLine(charge)
      ) {
        return false;
      }
      if (ExactTransactionLedger.isResubmissionAdjustment(charge)) {
        return this._isRebilledByResubmission(charge);
      }
      return !isCancelled;
    });
  }

  allocationsFor(charge: IExactV2Transaction): IExactV2TransactionAllocation[] {
    return (
      this._allocationsByChargeKey.get(
        ExactTransactionLedger._transactionKey(charge.id, charge.date)
      ) ?? []
    );
  }

  /**
   * The payment row an allocation draws from. A few Exact allocations carry a
   * payment date that disagrees with the payment row's own date, so when the
   * id and date miss, an id held by exactly one payment row still identifies it.
   */
  paymentRow(
    allocation: IExactV2TransactionAllocation
  ): IExactV2Transaction | undefined {
    const exactMatch = this._paymentRowsByKey.get(
      ExactTransactionLedger._transactionKey(
        allocation.paymentTransactionId,
        allocation.paymentDate
      )
    );
    if (exactMatch) {
      return exactMatch;
    }
    const sameIdRows = [...this._paymentRowsByKey.values()].filter(
      (paymentRow) => paymentRow.id === allocation.paymentTransactionId
    );
    return sameIdRows.length === 1 ? sameIdRows[0] : undefined;
  }

  /**
   * Merged databases reuse a line group id across courses of treatment, so a
   * line only belongs to the transaction when their courses agree. When the
   * course is reused too, the charge's date tells the lines apart.
   */
  linesForGroup(transaction: IExactV2Transaction): IExactV2InvoiceLine[] {
    if (!transaction.invoiceLineGroupId) {
      return [];
    }
    const lines = (
      this._invoiceLinesByGroupId.get(transaction.invoiceLineGroupId) ?? []
    ).filter(
      (line) =>
        !line.courseOfTreatmentId ||
        !transaction.courseOfTreatmentId ||
        line.courseOfTreatmentId === transaction.courseOfTreatmentId
    );
    if (!this._sharesLineGroupWithAnotherCharge(transaction)) {
      return lines;
    }
    return lines.filter((line) => line.completeDate === transaction.date);
  }

  private _categoriseTransactions(): ICategorisedTransactions {
    const charges: IExactV2Transaction[] = [];
    const deposits: IExactV2Transaction[] = [];

    for (const transaction of this._transactions) {
      switch (transaction.transactionType) {
        case ExactV2TransactionType.Charge:
        case ExactV2TransactionType.PaymentPlan:
          charges.push(transaction);
          break;
        case ExactV2TransactionType.Adjustment:
          if (ExactTransactionLedger.isInvoiceSideAdjustment(transaction)) {
            charges.push(transaction);
          }
          break;
        case ExactV2TransactionType.Deposit:
          deposits.push(transaction);
          break;
        case ExactV2TransactionType.TransferIn:
        case ExactV2TransactionType.TransferOut:
          if (this._isTransferOntoAccount(transaction)) {
            charges.push(transaction);
          }
          break;
        default:
          break;
      }
    }

    return { charges, deposits };
  }

  private _sharesLineGroupWithAnotherCharge(
    transaction: IExactV2Transaction
  ): boolean {
    if (transaction.transactionType !== ExactV2TransactionType.Charge) {
      return false;
    }
    return this._transactions.some(
      (other) =>
        other.transactionType === ExactV2TransactionType.Charge &&
        other.invoiceLineGroupId === transaction.invoiceLineGroupId &&
        other.courseOfTreatmentId === transaction.courseOfTreatmentId &&
        other.date !== transaction.date
    );
  }

  /**
   * A positive resubmission undoes a credit line. When that credit was
   * already spent settling another charge, nothing open is left to cancel,
   * so the adjustment itself is what the patient now owes. Credit Exact
   * over-allocated onto a paid charge shows as that charge's negative open
   * amount instead, and was never spent.
   */
  private _isRebilledByResubmission(adjustment: IExactV2Transaction): boolean {
    if (adjustment.amountOpen < EXACT_MINIMUM_OWING) {
      return false;
    }
    const creditLineIds = this._chargeIdsInLineGroup(adjustment);
    const chargeIdsPaidByCreditLines = new Set(
      this._allocations
        .filter((allocation) =>
          creditLineIds.has(allocation.paymentTransactionId)
        )
        .map((allocation) => allocation.chargeTransactionId)
    );
    const unresolvedAmountOpen = sumBy(
      this._transactions.filter(
        (transaction) =>
          creditLineIds.has(transaction.id) ||
          (chargeIdsPaidByCreditLines.has(transaction.id) &&
            transaction.amountOpen < 0)
      ),
      (transaction) => transaction.amountOpen
    );
    return (
      Math.abs(adjustment.amountOpen + unresolvedAmountOpen) >=
      EXACT_MINIMUM_OWING
    );
  }

  /**
   * A credit line Exact never allocated is account credit, not part of the
   * visit it was keyed against. Billing it too would count it twice.
   */
  private _isUnspentCreditLine(charge: IExactV2Transaction): boolean {
    return (
      charge.transactionType === ExactV2TransactionType.Charge &&
      charge.amount < 0 &&
      roundTo2Decimals(charge.amountOpen - charge.amount) === 0
    );
  }

  /**
   * When Exact cancels billed work it keeps the original lines and adds a
   * resubmission adjustment against the same line group. The adjustment is
   * either left open or allocated against the receipt that paid the charge,
   * freeing that receipt to be reallocated. A group may be cancelled in
   * several adjustments. If they add up to the group's line total, the whole
   * group was cancelled — don't bill it. If they only cover part, the lines
   * stay billed and the open amount becomes a write-off applied at the
   * account level.
   */
  private _getCancelledLineGroupIds(): Set<string> {
    const adjustmentsByLineGroup = groupBy(
      this._transactions.filter(
        (adjustment) =>
          adjustment.invoiceLineGroupId !== undefined &&
          ExactTransactionLedger.isResubmissionAdjustment(adjustment)
      ),
      (adjustment) => adjustment.invoiceLineGroupId
    );

    const cancelledLineGroupIds = new Set<string>();
    for (const [lineGroupId, adjustments] of Object.entries(
      adjustmentsByLineGroup
    )) {
      const cancelledAmount = roundTo2Decimals(
        sumBy(
          adjustments,
          (adjustment) =>
            Math.abs(adjustment.amountOpen) +
            this._amountReturnedToPayments(adjustment)
        )
      );
      if (cancelledAmount < EXACT_MINIMUM_OWING) {
        continue;
      }

      const lineTotal = Math.abs(
        roundTo2Decimals(
          sumBy(
            this.linesForGroup(adjustments[0]),
            (line) => line.patientAmount + line.patientGst
          )
        )
      );
      if (Math.abs(lineTotal - cancelledAmount) <= EXACT_MINIMUM_OWING) {
        cancelledLineGroupIds.add(lineGroupId);
      }
    }

    return cancelledLineGroupIds;
  }

  private _amountReturnedToPayments(adjustment: IExactV2Transaction): number {
    const groupChargeIds = this._chargeIdsInLineGroup(adjustment);
    return sumBy(
      this._allocations.filter(
        (allocation) =>
          allocation.paymentTransactionId === adjustment.id &&
          allocation.paymentDate === adjustment.date &&
          (allocation.chargeTransactionType ===
            ExactV2TransactionType.Receipt ||
            groupChargeIds.has(allocation.chargeTransactionId))
      ),
      (allocation) => Math.abs(allocation.amount)
    );
  }

  private _chargeIdsInLineGroup(adjustment: IExactV2Transaction): Set<string> {
    return new Set(
      this._transactions
        .filter(
          (transaction) =>
            transaction.transactionType === ExactV2TransactionType.Charge &&
            transaction.invoiceLineGroupId === adjustment.invoiceLineGroupId
        )
        .map((transaction) => transaction.id)
    );
  }

  private _collectChargeKeysUsedAsPayments(): Set<string> {
    const keys = new Set<string>();
    for (const allocation of this._allocations) {
      if (this._isChargeUsedAsPayment(allocation)) {
        keys.add(
          ExactTransactionLedger._transactionKey(
            allocation.paymentTransactionId,
            allocation.paymentDate
          )
        );
      }
    }
    return keys;
  }

  private _isChargeUsedAsPayment(
    allocation: IExactV2TransactionAllocation
  ): boolean {
    const paymentType = allocation.paymentTransactionType;
    const chargeType = allocation.chargeTransactionType;
    const paymentTransaction = this.paymentRow(allocation);

    const isChargeVsChargeReversal =
      paymentType === ExactV2TransactionType.Charge;
    const isResubmissionWriteOff =
      paymentTransaction !== undefined &&
      ExactTransactionLedger.isResubmissionAdjustment(paymentTransaction);
    const isAdjustmentVsAdjustment =
      paymentType === ExactV2TransactionType.Adjustment &&
      chargeType === ExactV2TransactionType.Adjustment;
    const isNegativeInvoiceSideWriteOff =
      paymentType === ExactV2TransactionType.Adjustment &&
      paymentTransaction?.adjustmentType ===
        ExactV2AdjustmentType.InvoiceSide &&
      paymentTransaction.amount < 0;

    return (
      isChargeVsChargeReversal ||
      isResubmissionWriteOff ||
      isAdjustmentVsAdjustment ||
      isNegativeInvoiceSideWriteOff
    );
  }

  private _buildAllocationIndex(): Map<
    string,
    IExactV2TransactionAllocation[]
  > {
    const allocationMap = new Map<string, IExactV2TransactionAllocation[]>();
    for (const allocation of this._allocations) {
      const key = ExactTransactionLedger._transactionKey(
        allocation.chargeTransactionId,
        allocation.chargeDate
      );
      const existing = allocationMap.get(key) ?? [];
      existing.push(allocation);
      allocationMap.set(key, existing);
    }
    return allocationMap;
  }

  private _buildPaymentRowIndex(): Map<string, IExactV2Transaction> {
    const transactionMap = new Map<string, IExactV2Transaction>();
    for (const transaction of this._transactions) {
      if (paymentRowTypes.includes(transaction.transactionType)) {
        transactionMap.set(
          ExactTransactionLedger._transactionKey(
            transaction.id,
            transaction.date
          ),
          transaction
        );
      }
    }
    return transactionMap;
  }

  private _isReattributedCharge(charge: IExactV2Transaction): boolean {
    return charge.sourcePatientId !== charge.patientId;
  }

  private _isTransferOntoAccount(transaction: IExactV2Transaction): boolean {
    return transaction.amount > 0 && !this._isReattributedCharge(transaction);
  }
}
